import type { Tenant, TenantSso } from '@titan/shared';
import { useState } from 'react';
import { useSession } from '../../session/SessionProvider.js';
import { Actions, Button, Card, ChipSelector, ConfirmButton, DefinitionList, Field, InlineError, Muted, Pill, PillRow, TextInput, ToggleRow } from '../../ui/index.js';
import styles from './Admin.module.css';

type Kind = 'saml' | 'oidc';

export interface SsoCardProps {
  tenant: Tenant;
  busy: boolean;
  run: (work: () => Promise<unknown>) => Promise<void>;
}

/**
 * Per-tenant single sign-on, configured by DinoCloud. SAML through the identity provider's
 * metadata URL; OIDC through issuer, client id and a secret that goes straight to Cognito.
 * Connecting, changing and removing wait for a second Platform Owner.
 */
export function SsoCard({ tenant, busy, run }: SsoCardProps) {
  const { api } = useSession();
  const current: TenantSso | undefined = tenant.ssoProvider;
  const [editing, setEditing] = useState(!current);
  const [type, setType] = useState<Kind>(current?.type ?? 'saml');
  const [label, setLabel] = useState(current?.label ?? '');
  const [metadataUrl, setMetadataUrl] = useState(current?.metadataUrl ?? '');
  const [issuer, setIssuer] = useState(current?.issuer ?? '');
  const [clientId, setClientId] = useState(current?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [enforce, setEnforce] = useState(current?.enforce ?? true);
  const [formError, setFormError] = useState<string | null>(null);

  const valid = label.trim().length >= 2 && (type === 'saml' ? /^https:\/\//.test(metadataUrl) : /^https:\/\//.test(issuer) && clientId.trim().length > 0 && (clientSecret.length > 0 || Boolean(current)));

  const save = () =>
    run(() =>
      api(`/admin/tenants/${tenant.id}/sso`, {
        method: 'PUT',
        body: JSON.stringify({
          type,
          label: label.trim(),
          enforce,
          ...(type === 'saml' ? { metadataUrl: metadataUrl.trim() } : { issuer: issuer.trim(), clientId: clientId.trim(), ...(clientSecret ? { clientSecret } : {}) }),
        }),
      }),
    ).then(() => {
      setClientSecret('');
      setEditing(false);
    });

  return (
    <Card
      title="Single sign-on"
      description="People at this tenant can sign in with their corporate identity provider. Only DinoCloud connects it. Changes wait for a second Platform Owner."
      actions={
        current && !editing ? (
          <PillRow>
            <Pill tone="positive">{current.type.toUpperCase()}</Pill>
            {current.enforce ? <Pill tone="warning">passwords disabled</Pill> : <Pill tone="neutral">passwords allowed</Pill>}
          </PillRow>
        ) : undefined
      }
    >
      {current && !editing && (
        <>
          <DefinitionList
            items={[
              { label: 'Provider', value: current.label },
              { label: 'Type', value: current.type === 'saml' ? 'SAML 2.0' : 'OpenID Connect' },
              ...(current.metadataUrl ? [{ label: 'Metadata URL', value: current.metadataUrl, mono: true }] : []),
              ...(current.issuer ? [{ label: 'Issuer', value: current.issuer, mono: true }] : []),
              ...(current.clientId ? [{ label: 'Client id', value: current.clientId, mono: true }] : []),
              { label: 'Cognito provider', value: current.providerName, mono: true },
              { label: 'Connected', value: `${new Date(current.configuredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} by ${current.configuredBy}` },
            ]}
          />
          <Muted small>
            Give the client’s identity team this service provider information: ACS URL https://auth.dev.titan.dinocloud.com/saml2/idpresponse, entity id urn:amazon:cognito:sp:{'{'}user pool id{'}'}. The email attribute must map to the standard email claim.
          </Muted>
          <Actions>
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)} disabled={busy}>
              Change
            </Button>
            <ConfirmButton
              label="Remove single sign-on"
              variant="destructive"
              tone="critical"
              disabled={busy}
              message={`People at ${tenant.name} go back to email, password and an authenticator. Those invited while SSO was on have no password yet and will need a reset. A second Platform Owner has to approve.`}
              confirmLabel="Ask for approval"
              onConfirm={() => run(() => api(`/admin/tenants/${tenant.id}/sso`, { method: 'DELETE' }))}
            />
          </Actions>
        </>
      )}

      {editing && (
        <>
          {formError && <InlineError>{formError}</InlineError>}
          <Field label="Protocol" chips>
            <ChipSelector<Kind>
              options={[
                { value: 'saml', label: 'SAML 2.0 (Okta, Entra ID, Google Workspace)' },
                { value: 'oidc', label: 'OpenID Connect' },
              ]}
              value={type}
              onChange={setType}
            />
          </Field>
          <Field label="What the sign-in button says" htmlFor="sso-label" hint="Usually the provider’s name, e.g. Okta or Microsoft Entra ID.">
            <TextInput id="sso-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Okta" />
          </Field>
          {type === 'saml' ? (
            <Field label="Identity provider metadata URL" htmlFor="sso-metadata" hint="From the client’s identity provider. Cognito reads the certificate and endpoints from it.">
              <TextInput id="sso-metadata" mono value={metadataUrl} onChange={(e) => setMetadataUrl(e.target.value)} placeholder="https://company.okta.com/app/xxxx/sso/saml/metadata" />
            </Field>
          ) : (
            <>
              <Field label="Issuer URL" htmlFor="sso-issuer">
                <TextInput id="sso-issuer" mono value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder="https://login.microsoftonline.com/{tenant}/v2.0" />
              </Field>
              <div className={styles.inline}>
                <TextInput mono value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Client id" aria-label="Client id" />
                <TextInput mono type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder={current ? 'Client secret (leave empty to keep)' : 'Client secret'} aria-label="Client secret" autoComplete="off" />
              </div>
              <Muted small>The secret goes straight to Cognito. Titan never stores it.</Muted>
            </>
          )}
          <ToggleRow label="Require single sign-on" note="When on, nobody at this tenant can sign in with a password. Turn it off during a rollout." checked={enforce} onChange={setEnforce} />
          <Actions>
            <ConfirmButton
              label={current ? 'Save changes' : 'Connect single sign-on'}
              variant="primary"
              size="md"
              disabled={busy || !valid}
              message={`${tenant.name} will sign in through ${label.trim() || 'the provider'} (${type.toUpperCase()})${enforce ? ' and passwords will be disabled' : ''}. A second Platform Owner has to approve before it takes effect.`}
              confirmLabel="Ask for approval"
              onConfirm={() => {
                setFormError(null);
                return save();
              }}
            />
            {current && (
              <Button variant="secondary" onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </Button>
            )}
          </Actions>
        </>
      )}
    </Card>
  );
}
